// @ts-nocheck
import { useRef, useState, useEffect } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useSelector, useDispatch } from 'react-redux';

import API_ENDPOINTS from '../../const/ApiEndPoints';
import SwalToast from '../../components/Common/swalToast';
import { isCommonAuthEnabled } from '../../const/commonAuthConfig';
import {
  bumpCounter,
  addEntityEvent,
  dbSyncEvent,
  SSE_EVENTS,
  COUNTER_EVENTS,
  ENTITY_EVENTS,
  ENTITY_PAGE_REFRESH_EVENTS,
  notificationAndPageRefreshEvent,
} from '../../store/slices/sseSlice';

/**
 * Reason, we are using both of these error classes, fetchEventSource library will use these
 * in retrying logic.
 * we cannot see these errors in the console
 */
class RetriableError extends Error {}
class FatalError extends Error {}

export default function useSSEConnect() {
  const dispatch = useDispatch();
  const { isAuthenticate } = useSelector((state) => state.users);
  const controllerRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  const handleOnMessageEvent = (response) => {
    const sseType = response?.type;

    if (sseType === SSE_EVENTS.AUTOMATION_NOTIFICATION) {
      SwalToast({
        icon: 'report',
        persist: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        title: JSON.stringify(response?.data), //we can't send an object in title, so converting it
      });
    } else if (COUNTER_EVENTS.has(sseType)) {
      dispatch(bumpCounter(sseType));
    } else if (ENTITY_EVENTS.has(sseType)) {
      dispatch(
        addEntityEvent({
          sseType,
          ...response,
        })
      );
    } else if (sseType === SSE_EVENTS.DB_SYNC) {
      dispatch(dbSyncEvent({ data: response?.data, sseType }));
    } else if (ENTITY_PAGE_REFRESH_EVENTS.has(sseType)) {
      if (sseType === SSE_EVENTS.AI_GOVERNANCE_DISCOVER) {
        SwalToast({
          icon: response?.data?.event_status === 'Success' ? 'success' : 'info',
          persist: true,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          title: response?.data?.msg,
        });
      }
      dispatch(
        notificationAndPageRefreshEvent({
          sseType,
          ...response,
        })
      );
    }
  };

  useEffect(() => {
    if (!isAuthenticate) return;

    const org = localStorage.getItem('REALM_ID');

    controllerRef.current = new AbortController();
    const abortSignal = controllerRef.current.signal;

    const connect = async () => {
      try {
        await fetchEventSource(
          `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.NOTIFICATIONS.SSE}`,
          {
            signal: abortSignal,
            /**
             * why are we using this custom fetch hook?
             * coz on reconnection fetchEventSource lib try to reconnect on old headers
             * internally this library also uses Native fetch, now we want to set the latest header in request
             * by using fetch hook provided by fetchEventSource lib,
             * below url is the same as this -> `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.NOTIFICATIONS.SSE}`,
             * now what we are doing here is over-riding the headers, will take the latest token on every connection and reconnection
             */
            async fetch(url, init) {
              const latestToken = localStorage.getItem('access_token');

              // Use Bearer token for common auth, JWT for regular auth
              const authHeader = isCommonAuthEnabled()
                ? `Bearer ${latestToken}`
                : `JWT ${latestToken}`;

              return fetch(url, {
                ...init,
                headers: {
                  ...init.headers,
                  Authorization: authHeader,
                  'X-Org': org,
                },
              });
            },
            async onopen(response) {
              if (
                response.ok &&
                response.headers
                  .get('content-type')
                  ?.includes('text/event-stream')
              ) {
                setIsConnected(true);
              } else {
                throw new RetriableError(`Server error: ${response.status}`);
              }
            },
            onmessage(event) {
              if (event?.event === 'FatalError') {
                throw new FatalError(event.data);
              }
              // the server is sending ping data on a regular interval to keep living connection,
              // so avoid this ping message
              if (event?.data === 'ping') {
                return;
              }
              try {
                const parsedData = JSON.parse(event?.data);
                handleOnMessageEvent(parsedData);
              } catch {
                handleOnMessageEvent(event.data);
              }
            },
            onclose() {
              throw new RetriableError('Connection closed unexpectedly');
            },
            onerror(err) {
              if (err instanceof FatalError) {
                throw err;
              } else {
                // return 60 sec retry interval for retriable errors
                return 60000;
              }
            },
            openWhenHidden: true,
          }
        );
      } catch (err) {
        console.warn('SSE connection failed:', err);
      }
    };

    connect();

    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
        setIsConnected(false);
      }
    };
  }, [isAuthenticate]);

  return { isConnected };
}
